<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import CardField from "../cards/CardField.vue";
import CameraOverlay from "@/components/field-observations/CameraOverlay.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { usePwaController } from "@/composable/usePWA";
import type { ExistingMedia } from "@/composable/useFieldObservationForm";

/**
 * Photos d'une observation, dans sa carte : vignettes (suppression, n° de
 * marqueur assignable), une case d'attente par marqueur du plan encore sans
 * photo, et la tuile « + ». Les photos sont prises en live sur le terrain :
 * leur nom ne veut rien dire, c'est l'inspecteur qui désigne quelle photo
 * illustre quel marqueur (`photoNumber` de l'image = numéro du marqueur).
 * Ajout par la caméra sur tablette, le sélecteur de fichiers sur desktop, ou
 * un glisser-déposer d'images sur une case.
 */

defineProps<{
  photos: ExistingMedia[];
  isUploading: boolean;
  /** Numéros des marqueurs photo rattachés à l'observation. */
  markerNumbers: number[];
  /** n° de marqueur assigné à chaque photo (par id d'image). */
  photoNumberById: Map<number, number | null>;
  /** Marqueurs encore sans photo — une case d'attente chacun. */
  uncoveredMarkers: number[];
}>();

const emit = defineEmits<{
  /** Photos à envoyer, associées d'office au marqueur `markerNumber` s'il est donné. */
  (e: "upload", files: File[], markerNumber: number | null): void;
  (e: "delete", photoId: number): void;
  (e: "assign", photoId: number, markerNumber: number | null): void;
}>();

const { t } = useTranslation();
const { isMobileOrTablet } = usePwaController();

// `pendingMarkerNumber` : ajout déclenché depuis un marqueur non couvert — la
// photo envoyée est directement associée à ce numéro.
const photoInput = ref<HTMLInputElement | null>(null);
const showCameraOverlay = ref(false);
const pendingMarkerNumber = ref<number | null>(null);

const openPhotoPicker = (markerNumber: number | null = null): void => {
  pendingMarkerNumber.value = markerNumber;
  if (isMobileOrTablet()) showCameraOverlay.value = true;
  else photoInput.value?.click();
};

/** Consomme le n° armé à l'ouverture du sélecteur / de la caméra. */
const consumePendingMarker = (): number | null => {
  const n = pendingMarkerNumber.value;
  pendingMarkerNumber.value = null;
  return n;
};

const handleCameraCaptured = (files: File[]): void => {
  showCameraOverlay.value = false;
  emit("upload", files, consumePendingMarker());
};

const handlePhotoChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const files = input.files?.length ? Array.from(input.files) : [];
  input.value = "";
  if (files.length) emit("upload", files, consumePendingMarker());
};

// ── Glisser-déposer d'images (desktop) ──────────────────────────────────────
// Cibles : la tuile « + » (photo sans numéro) et les cases d'attente d'un
// marqueur (photo directement associée au numéro).

/** Cible survolée : n° de marqueur, "new" pour la tuile « + », ou null. */
const dragOverTarget = ref<number | "new" | null>(null);

const onDragOver = (target: number | "new"): void => {
  dragOverTarget.value = target;
};

const onDragLeave = (event: DragEvent, target: number | "new"): void => {
  // Passer sur un enfant déclenche aussi `dragleave` : on ne réinitialise que
  // si le curseur quitte réellement la tuile.
  const related = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement;
  if (related && current.contains(related)) return;
  if (dragOverTarget.value === target) dragOverTarget.value = null;
};

const onDropPhotos = (event: DragEvent, markerNumber: number | null): void => {
  dragOverTarget.value = null;
  const files = Array.from(event.dataTransfer?.files ?? []).filter((file) =>
    file.type.startsWith("image/"),
  );
  if (files.length) emit("upload", files, markerNumber);
};
</script>

<template>
  <CardField :label="t('dashboard:fieldObservations.sections.photos')">
    <template #aside>
      <span v-if="isUploading" class="spinner-border spinner-border-sm" />
    </template>

    <div class="media-grid">
      <div v-for="photo in photos" :key="photo.id" class="media-item">
        <div v-if="photo.isLoading" class="media-loading">
          <span class="spinner-border spinner-border-sm" />
        </div>
        <img v-else :src="photo.url" alt="Photo" />
        <button
          type="button"
          class="btn-remove-media"
          @click="emit('delete', photo.id)"
        >
          <PhosphorIcon name="trash" :size="12" weight="bold" />
        </button>

        <!-- N° de marqueur de la photo (assignation manuelle) -->
        <el-dropdown
          v-if="markerNumbers.length"
          trigger="click"
          class="photo-number-dropdown"
          @click.stop
        >
          <button type="button" class="photo-number-badge" @click.stop>
            {{
              photoNumberById.get(photo.id) != null
                ? `n°${photoNumberById.get(photo.id)}`
                : "n° ?"
            }}
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="n in markerNumbers"
                :key="n"
                @click="emit('assign', photo.id, n)"
              >
                <span class="d-inline-flex align-items-center gap-2">
                  n°{{ n }}
                  <PhosphorIcon
                    v-if="photoNumberById.get(photo.id) === n"
                    name="check"
                    :size="13"
                    weight="bold"
                  />
                </span>
              </el-dropdown-item>
              <el-dropdown-item divided @click="emit('assign', photo.id, null)">
                {{ t("canvas:observations.photos.noNumber") }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <!-- Cases d'attente : marqueurs qui n'ont pas encore leur photo
           (clic ou glisser-déposer d'une image) -->
      <button
        v-for="n in uncoveredMarkers"
        :key="`placeholder-${n}`"
        type="button"
        class="media-placeholder"
        :class="{ 'drag-over': dragOverTarget === n }"
        :title="t('canvas:observations.photos.addForMarker', { n })"
        @click="openPhotoPicker(n)"
        @dragover.prevent="onDragOver(n)"
        @dragenter.prevent
        @dragleave="onDragLeave($event, n)"
        @drop.prevent="onDropPhotos($event, n)"
      >
        <PhosphorIcon name="camera-plus" :size="18" />
        <span>n°{{ n }}</span>
      </button>

      <button
        type="button"
        class="add-media-btn"
        :class="{ 'drag-over': dragOverTarget === 'new' }"
        @click="openPhotoPicker(null)"
        @dragover.prevent="onDragOver('new')"
        @dragenter.prevent
        @dragleave="onDragLeave($event, 'new')"
        @drop.prevent="onDropPhotos($event, null)"
      >
        <PhosphorIcon name="plus" :size="18" />
      </button>
    </div>

    <input
      ref="photoInput"
      type="file"
      accept="image/*"
      multiple
      class="d-none"
      @change="handlePhotoChange"
    />

    <CameraOverlay
      v-if="showCameraOverlay"
      @close="showCameraOverlay = false"
      @captured="handleCameraCaptured"
    />
  </CardField>
</template>

<style lang="scss" scoped>
// Vignettes de taille bornée : le volet se redimensionne, et trois colonnes
// fixes donnaient des photos immenses une fois élargi. Ici la grille en met
// autant par ligne qu'il en tient (3 à la largeur par défaut du volet).
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 4px;
}

.media-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: var(--bs-border-radius);
  overflow: hidden;
  background-color: var(--bs-tertiary-bg);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.media-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove-media {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.photo-number-dropdown {
  position: absolute;
  bottom: 2px;
  left: 2px;
}

.photo-number-badge {
  border: none;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 2px 9px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }
}

.media-placeholder {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed var(--bs-primary);
  border-radius: var(--bs-border-radius);
  background-color: color-mix(in srgb, var(--bs-primary) 8%, transparent);
  color: var(--bs-primary);
  font-size: 0.78rem;
  font-weight: 500;

  &.drag-over {
    background-color: color-mix(in srgb, var(--bs-primary) 18%, transparent);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: color-mix(in srgb, var(--bs-primary) 18%, transparent);
    }
  }

  &.drag-over {
    border-style: solid;
    border-width: 2px;
  }
}

.add-media-btn {
  aspect-ratio: 1;
  border: 1px dashed var(--bs-secondary-color);
  border-radius: var(--bs-border-radius);
  background-color: transparent;
  color: var(--bs-secondary-color);
  display: flex;
  align-items: center;
  justify-content: center;

  &.drag-over {
    border-color: var(--bs-primary);
    color: var(--bs-primary);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--bs-primary);
      color: var(--bs-primary);
    }
  }

  &.drag-over {
    border-style: solid;
    border-width: 2px;
    background-color: color-mix(in srgb, var(--bs-primary) 15%, transparent);
  }
}
</style>
